import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { WhatsAppDemo } from "@/components/WhatsAppDemo";
import { AccessibilityToolbar } from "@/components/AccessibilityToolbar";
import { SkipToMain } from "@/components/SkipLink";
import { AppLogo } from "@/components/AppLogo";
import { useUser } from "@/contexts/UserContext";
import dashboardHero from "@/assets/dashboard-hero.jpg";
import { 
  ArrowRight, 
  BarChart3, 
  Calendar, 
  CreditCard, 
  DollarSign, 
  PieChart, 
  Receipt, 
  Shield, 
  Smartphone, 
  TrendingUp,
  CheckCircle,
  Star,
  Users,
  Zap,
  Accessibility
} from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, loading } = useUser();
  const [accessibilityToolbarOpen, setAccessibilityToolbarOpen] = useState(false);
  const isRTL = i18n.language === 'he';

  // Note: Removed automatic redirect to dashboard
  // Users should only be redirected when they explicitly click login

  const handleLogin = () => {
    navigate('/login');
  };

  const handleGetStarted = () => {
    navigate('/login');
  };

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <SkipToMain />
      
      {/* Navigation */}
      <nav 
        id="main-navigation"
        className="border-b border-border bg-background/85 backdrop-blur-md sticky top-0 z-50"
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <AppLogo
                variant="chrome"
                frameClassName="inline-flex h-10 border-border/60 bg-background px-1.5"
                className="h-9 w-auto max-h-full max-w-[7rem]"
                alt=""
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
                FINGENT
              </span>
            </div>
            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
              {/* Accessibility Button - Most prominent */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAccessibilityToolbarOpen(!accessibilityToolbarOpen)}
                aria-label={t('accessibility.toolbar.title', 'נגישות')}
                aria-expanded={accessibilityToolbarOpen}
                className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-950"
              >
                <Accessibility className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('accessibility.toolbar.title', 'נגישות')}</span>
              </Button>
              
              <LanguageSwitcher />
              {/* <Button variant="ghost" className="hidden sm:inline-flex">
                {t('landing.nav.features')}
              </Button>
              <Button variant="ghost" className="hidden sm:inline-flex">
                {t('landing.nav.pricing')}
              </Button>
              <Button variant="ghost" className="hidden sm:inline-flex">
                {t('landing.nav.about')}
              </Button> */}
              <Button onClick={handleLogin} className="bg-primary hover:bg-primary/90">
                {t('auth.login')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main id="main-content" role="main" tabIndex={-1}>
        <section className="relative overflow-hidden py-20 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8 items-center">
            <div className="sm:text-center md:max-w-2xl md:mx-auto lg:col-span-6 lg:text-left">
              <Badge variant="secondary" className="mb-4 bg-primary/10 text-primary border-primary/20">
                <Zap className={`h-3 w-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                {t('landing.hero.badge')}
              </Badge>
              
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl lg:text-6xl">
                {t('landing.hero.titlePart1')}{" "}
                <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
                  {t('landing.hero.titlePart2')}
                </span>
              </h1>
              
              <p className="mt-6 text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                {t('landing.hero.subtitle')}
              </p>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:justify-center lg:justify-start">
                <Button 
                  size="lg" 
                  onClick={handleGetStarted}
                  className="bg-primary hover:bg-primary/90 text-white px-8 py-4 text-lg"
                >
                  {t('landing.hero.getStarted')}
                  <ArrowRight className={`h-5 w-5 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="px-8 py-4 text-lg border-primary text-primary hover:bg-primary/5"
                  onClick={() => {
                    // Scroll to WhatsApp demo section
                    document.getElementById('whatsapp-demo')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {t('landing.hero.watchDemo')}
                </Button>
              </div>
              
              {/* <div className="mt-8 flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400 sm:justify-center lg:justify-start">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  No credit card required
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Free for personal use
                </div>
              </div> */}
            </div>
            
            <div className="mt-16 sm:mt-24 lg:mt-0 lg:col-span-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-blue-600/20 rounded-3xl blur-3xl"></div>
                <img
                  src={dashboardHero}
                  alt={t('landing.dashboardPreview')}
                  className="relative rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Demo Section */}
      <WhatsAppDemo />

      {/* Features Section */}
      <section className="py-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
              {t('landing.features.title')}
            </h2>
            <p className="mt-4 text-xl text-gray-600 dark:text-gray-300">
              {t('landing.features.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <Card className="border-border/90 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                                  <AppLogo
                                    variant="chrome"
                                    frameClassName="mb-4 h-12 w-12 border-border/70 bg-muted"
                                    className="h-10 w-10"
                                    alt=""
                                  />
                <CardTitle>{t('landing.features.expenseTracking.title')}</CardTitle>
                <CardDescription>
                  {t('landing.features.expenseTracking.description')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Feature 2 */}
            <Card className="border-border/90 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-4">
                  <PieChart className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <CardTitle>{t('landing.features.analytics.title')}</CardTitle>
                <CardDescription>
                  {t('landing.features.analytics.description')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Feature 3 */}
            <Card className="border-border/90 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4">
                  <Calendar className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <CardTitle>{t('landing.features.recurring.title')}</CardTitle>
                <CardDescription>
                  {t('landing.features.recurring.description')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Feature 4 */}
            <Card className="border-border/90 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mb-4">
                  <CreditCard className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <CardTitle>{t('landing.features.payments.title')}</CardTitle>
                <CardDescription>
                  {t('landing.features.payments.description')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Feature 5 */}
            <Card className="border-border/90 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
                <CardTitle>{t('landing.features.insights.title')}</CardTitle>
                <CardDescription>
                  {t('landing.features.insights.description')}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Feature 6 */}
            <Card className="border-border/90 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <CardTitle>{t('landing.features.security.title')}</CardTitle>
                <CardDescription>
                  {t('landing.features.security.description')}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center text-white">
            <div>
              <div className="flex items-center justify-center mb-2">
                <Users className="h-8 w-8 mr-2" />
                <span className="text-4xl font-bold">10K+</span>
              </div>
              <p className="text-xl opacity-90">{t('landing.stats.users')}</p>
            </div>
            <div>
              <div className="flex items-center justify-center mb-2">
                <DollarSign className="h-8 w-8 mr-2" />
                <span className="text-4xl font-bold">$50M+</span>
              </div>
              <p className="text-xl opacity-90">{t('landing.stats.expenses')}</p>
            </div>
            <div>
              <div className="flex items-center justify-center mb-2">
                <Star className="h-8 w-8 mr-2" />
                <span className="text-4xl font-bold">4.9/5</span>
              </div>
              <p className="text-xl opacity-90">{t('landing.stats.rating')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-transparent">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl mb-6">
            {t('landing.cta.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Join thousands of users who have transformed their financial lives with FINGENT
          </p>
          <Button 
            size="lg" 
            onClick={handleGetStarted}
            className="bg-primary hover:bg-primary/90 text-white px-12 py-4 text-lg"
          >
            {t('landing.cta.button')}
            <ArrowRight className={`h-5 w-5 ${isRTL ? 'mr-2' : 'ml-2'}`} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card/50 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="col-span-1 md:col-span-2">
                              <div className="flex items-center space-x-2 mb-4">
                  <AppLogo
                    variant="chrome"
                    frameClassName="inline-flex h-10 border-border/60 bg-muted px-1.5"
                    className="h-9 w-auto max-h-full max-w-[7rem]"
                    alt=""
                  />
                  <span className="text-2xl font-bold">FINGENT</span>
                </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('landing.footer.description')}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('landing.footer.support')}</h3>
              <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                <li>
                  <button onClick={() => navigate('/contact')} className="hover:text-primary transition-colors">
                    {t('landing.footer.contact')}
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/privacy')} className="hover:text-primary transition-colors">
                    {t('landing.footer.privacy')}
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/terms')} className="hover:text-primary transition-colors">
                    {t('landing.footer.terms')}
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/accessibility-statement')} className="hover:text-primary transition-colors">
                    {t('landing.footer.accessibility')}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t mt-8 pt-8 text-center text-gray-600 dark:text-gray-400">
            <p>{t('landing.footer.copyright')}</p>
          </div>
        </div>
      </footer>
      </main>
      
      {/* Floating Accessibility Button */}
      <div className="fixed bottom-6 left-6 z-50">
        <Button
          onClick={() => setAccessibilityToolbarOpen(!accessibilityToolbarOpen)}
          className="w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
          aria-label={t('accessibility.toolbar.title', 'נגישות')}
          aria-expanded={accessibilityToolbarOpen}
        >
          <Accessibility className="h-6 w-6" />
        </Button>
      </div>

      {/* Accessibility Toolbar */}
      <AccessibilityToolbar 
        isOpen={accessibilityToolbarOpen}
        onClose={() => setAccessibilityToolbarOpen(false)}
      />
    </div>
  );
} 